/**
 * How it works on the linkage editor (Feature 72, 5.1, 5.2): the tour's six
 * steps, as the Tour artboard lists them, and whether this browser has seen
 * it. The tour opens by itself until it is closed or finished once here
 * (D12); How it works runs it again from the start (D7).
 *
 * How it works on the Linkages list opens a new template with `?tour=1`,
 * which starts the tour and is dropped from the address once read.
 *
 * A new template has no step and no link, so the steps about a step and a
 * gate select the first of each when there is one, and otherwise point at
 * the inspector's panel and say what will appear there.
 */
import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import type { TourStep } from '../../ui/Tour.js';
import { NEW_LINKAGE_PATH } from '../automation/paths.js';
import type { Draft } from './draft.js';
import type { Selection } from './selection.js';

const SEEN_KEY = 'optum.linkages.tour.seen';
const TOUR_PARAM = 'tour';

/** The editor on a new template, with the tour running: where How it works on the list leads. */
export const NEW_WITH_TOUR = `${NEW_LINKAGE_PATH}?${TOUR_PARAM}=1`;

/** Whether this browser has seen the tour; a browser without storage has not. */
function seen(): boolean {
  try {
    return window.localStorage.getItem(SEEN_KEY) === 'true';
  } catch {
    return false;
  }
}

function markSeen(): void {
  try {
    window.localStorage.setItem(SEEN_KEY, 'true');
  } catch {
    // Storage unavailable (private mode, quota): the tour shows again next time.
  }
}

/**
 * The tour's state: open by itself on a first visit, or when the address asks
 * for it, and remembered once closed or finished.
 */
export function useLinkageTour(): { open: boolean; start: () => void; close: () => void } {
  const [params, setParams] = useSearchParams();
  const [open, setOpen] = useState(() => !seen());
  const start = useCallback(() => setOpen(true), []);
  useEffect(() => {
    if (params.get(TOUR_PARAM) !== '1') return;
    setOpen(true);
    setParams(
      (next) => {
        next.delete(TOUR_PARAM);
        return next;
      },
      { replace: true },
    );
  }, [params, setParams]);
  const close = useCallback(() => {
    markSeen();
    setOpen(false);
  }, []);
  return { open, start, close };
}

/** The six steps over the editor; the third to fifth select what they describe. */
export function linkageTourSteps(
  draft: Draft,
  onSelect: (selection: Selection) => void,
): TourStep[] {
  const step = draft.steps[0];
  const link = draft.links[0];
  const selectLink = link
    ? () => onSelect({ kind: 'edge', from: link.from, to: link.to })
    : undefined;
  return [
    {
      target: 'canvas',
      title: 'The canvas',
      body: (
        <>
          <span className="mono">first</span> is the capability the chain starts from: click it to
          set how it starts. Steps are nodes; drag from a step&apos;s right side to another&apos;s
          left to link them.
        </>
      ),
    },
    {
      target: 'add-step',
      title: 'Add a step',
      body: 'Choose a capability from the list; its step lands right of the chain, ready to link.',
    },
    {
      target: 'inspector',
      title: 'A step and its mode',
      body: step
        ? 'A selected step maps earlier inputs and results into its own input, and its mode says how its result comes back: merged, written back, or as a task of its own.'
        : 'Select a step and it opens here: what it maps into its input, and its mode, merged, written back, or a task of its own.',
      onEnter: step ? () => onSelect({ kind: 'node', id: step.name }) : undefined,
    },
    {
      target: link ? 'gate' : 'canvas',
      title: 'A gate on every link',
      body: 'Each link carries a gate that decides whether the step after it runs once the node before has ended. A new gate is empty, and an empty gate cannot be saved.',
      onEnter: selectLink,
    },
    {
      target: 'inspector',
      title: 'Conditions, or pass automatically',
      body: link
        ? "A gate's conditions read the node before it first, its input or its output, then earlier nodes. Pass automatically lets it through without reading them."
        : 'Select a link and its gate opens here: conditions on the node before it, its input or its output, or Pass automatically.',
      onEnter: selectLink,
    },
    {
      target: 'save',
      title: 'Save',
      body: 'A save is judged whole. A refused save marks each problem on its step or its link, so you can see what to fix.',
    },
  ];
}
